import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Plus, Pencil, Trash2, Eye } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { useAssets, useDeleteAsset } from '@/hooks'
import {
  PageHeader,
  EmptyState,
  DataTable,
  SortableHeader,
  ConfirmDialog,
} from '@/components'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Asset } from '@/types'
import { AssetFormDialog } from './AssetFormDialog'

export function AssetsPage() {
  const { user } = useAuth()
  const { effectiveOrgId } = useOrg()
  const { data: assets, isLoading, error } = useAssets(effectiveOrgId)
  const deleteMutation = useDeleteAsset(effectiveOrgId)

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editAsset, setEditAsset] = useState<Asset | null>(null)
  const [deleteAsset, setDeleteAsset] = useState<Asset | null>(null)

  const canEdit = user?.role === 'super_admin' || user?.role === 'admin'

  // Super admin with no org selected
  if (user?.role === 'super_admin' && !effectiveOrgId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Assets"
          description="Manage asset templates for cleaning checklists"
        />
        <EmptyState
          title="No Organization Selected"
          description="Select an organization using the switcher in the header to view assets."
        />
      </div>
    )
  }

  const columns: ColumnDef<Asset>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <SortableHeader column={column}>Name</SortableHeader>
      ),
      cell: ({ row }) => (
        <Link
          to={`/assets/${row.original.id}`}
          className="font-medium text-primary hover:underline"
        >
          {row.getValue('name')}
        </Link>
      ),
    },
    {
      accessorKey: 'description',
      header: ({ column }) => (
        <SortableHeader column={column}>Description</SortableHeader>
      ),
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.getValue('description') || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <SortableHeader column={column}>Created</SortableHeader>
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue('created_at'))
        return date.toLocaleDateString()
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/assets/${row.original.id}`}>
                <Eye className="mr-2 h-4 w-4" />
                View
              </Link>
            </DropdownMenuItem>
            {canEdit && (
              <>
                <DropdownMenuItem onClick={() => setEditAsset(row.original)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDeleteAsset(row.original)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  if (error) {
    return (
      <div className="text-destructive">
        Failed to load assets: {error.message}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Assets"
        description="Manage asset templates for cleaning checklists"
        actions={
          canEdit && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Asset
            </Button>
          )
        }
      />

      {isLoading ? (
        <div className="text-muted-foreground">Loading assets...</div>
      ) : assets && assets.length > 0 ? (
        <DataTable
          columns={columns}
          data={assets}
          filterColumn="name"
          filterPlaceholder="Filter by name..."
        />
      ) : (
        <EmptyState
          title="No assets"
          description="Create asset templates that can be used in cleaning checklists for rooms."
          action={
            canEdit && (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Asset
              </Button>
            )
          }
        />
      )}

      <AssetFormDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {editAsset && (
        <AssetFormDialog
          open={!!editAsset}
          onOpenChange={(open) => !open && setEditAsset(null)}
          asset={editAsset}
        />
      )}

      <ConfirmDialog
        open={!!deleteAsset}
        onOpenChange={(open) => !open && setDeleteAsset(null)}
        title="Delete Asset"
        description={`Are you sure you want to delete "${deleteAsset?.name}"? This will remove it from all room checklists. This action cannot be undone.`}
        confirmText="Delete"
        destructive
        onConfirm={() => {
          if (deleteAsset) {
            deleteMutation.mutate(deleteAsset.id, {
              onSuccess: () => setDeleteAsset(null),
            })
          }
        }}
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
